import React, { useCallback, useState } from 'react';
import {Typography} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useForm } from 'react-hook-form';

import {
  getDemographicsInputValues,
  getDemographicsSummary,
} from '@components/Pages/Events/eventFunctions';
import Alerts from '@components/common/Alerts';
import ModalForm from '@components/common/hooks/useFormModal/ModalForm';
import useDemographicOptionsQuery from "@components/common/hooks/api/useDemographicOptionsQuery";
import EventTotalParticipants from "@components/ContentComponents/Events/EventTotalParticipants";
import { getArrayMsg, scrollToAlert } from '@lib/utils';
import EventDemographicsFormFields from './EventDemographicsFormFields';
import EventDemographicFormInstructions from './EventDemographicsFormInstructions';

/**
 * Form fields for the Event Demographics form. This does not include a <FormContainer> or any submit logic,
 * so it can be used in a form by itself, or as part of another, larger form.
 */
const EventDemographicsForm = ({
  onSubmit,
  submitRef,
  event,
  showParticipants = false,
}) => {
  const [pageMessages, setMessages] = useState(null);
  const [pageErrorMessages, setErrorMessages] = useState(null);

  const setPageMessage = useCallback((msg) => setMessages(getArrayMsg(msg)), []);
  const setPageErrorMessage = useCallback((msg) => setErrorMessages(getArrayMsg(msg)), []);

  const { data: demographicOptions, ...demographicOptionsQuery } = useDemographicOptionsQuery();

  const formContext = { pageMessages, pageErrorMessages, setPageMessage, setPageErrorMessage };
  const form = useForm({
    mode: 'onSubmit',
    // Note: Use 'values' instead of 'defaultValues' to avoid issues with the values being cached.
    values: getDemographicsInputValues(event, demographicOptions),
  });

  const onSubmitValidator = useCallback((values) => {
    setErrorMessages(null);
    const summary = getDemographicsSummary(event, values);
    if (!summary.valid) {
      setPageErrorMessage(<Typography variant="body1">
        The sums in the categories below require your attention.
      </Typography>);
      scrollToAlert();
      return false;
    }
    return onSubmit(values);
  }, [event, onSubmit]);

  return (
    <>
      <EventDemographicFormInstructions event={event} />
      <Grid container spacing={1} sx={{ marginBottom: '8px' }}>
        <Grid xs={12} id="modal-alerts">
          <Alerts modalContext={formContext} />
        </Grid>
      </Grid>
      {showParticipants && (
        <EventTotalParticipants event={event} />
      )}
      <ModalForm
        form={form}
        onSubmit={onSubmitValidator}
        submitRef={submitRef}
        formContext={formContext}
      >
        <EventDemographicsFormFields form={form} event={event} showSummary showCompleteConfirmation />
      </ModalForm>
    </>
  );
}
export default EventDemographicsForm;
